import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';
import type { ArtistAdminListItem } from '@/lib/api-admin/types';
import {
  Button,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { ArtistStatusPill } from './ArtistStatusPill';
import { ArtistTypeBadge } from './ArtistTypeBadge';
import { ArtistStorefrontOpenLink } from './ArtistStorefrontLinks';
import { ArtistDisplayedBadge } from './ArtistDisplayedBadge';
import { artistDisplayedOf } from '../lib/artistDisplayed';

export const ARTIST_SORTABLE_COLUMNS = new Set([
  'stageName',
  'createdAt',
  'updatedAt',
  'productCount',
]);

export interface ArtistTableProps {
  items: ReadonlyArray<ArtistAdminListItem>;
  sort: string;
  onSortChange: (sort: string) => void;
  /**
   * 246: 목록에서 직접 아티스트 삭제(소프트 삭제 = 비활성). 활성(ACTIVE) 행에만 노출하며,
   * 행 클릭(상세 진입)과 분리하기 위해 onClick stopPropagation 한다. 미지정 시 액션 열 미노출.
   */
  onDelete?: (artistId: string) => void;
  deletingId?: string | null;
  /** 영구 삭제(hard delete) — archive 옆에 별도 노출. 미지정 시 삭제 버튼 미노출. */
  onPurge?: (artistId: string) => void;
  purgingId?: string | null;
  /** QA-593 쇼핑몰 노출/숨김 전환. 미지정 시 버튼 미노출. */
  onToggleDisplayed?: (artistId: string, next: boolean) => void;
  togglingDisplayedId?: string | null;
}

export const parseArtistSort = (sort: string): { id: string; dir: 'asc' | 'desc' } => {
  const parts = sort.split(',');
  const id = parts[0] && parts[0].length > 0 ? parts[0] : 'createdAt';
  const dir = parts[1] === 'asc' ? 'asc' : 'desc';
  return { id, dir };
};

export const toggleArtistSort = (current: string, columnId: string): string => {
  const { id, dir } = parseArtistSort(current);
  if (id !== columnId) return `${columnId},desc`;
  return `${columnId},${dir === 'desc' ? 'asc' : 'desc'}`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const ArtistTable = ({
  items,
  sort,
  onSortChange,
  onDelete,
  deletingId = null,
  onPurge,
  purgingId = null,
  onToggleDisplayed,
  togglingDisplayedId = null,
}: ArtistTableProps) => {
  const { t } = useTranslation('artists');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseArtistSort(sort);
  const deletable = Boolean(onDelete);
  const purgeable = Boolean(onPurge);
  const displayToggleable = Boolean(onToggleDisplayed);
  const hasActions = deletable || purgeable || displayToggleable;

  const columns = useMemo<ColumnDef<ArtistAdminListItem>[]>(
    () => [
      {
        id: 'profile',
        header: () => '',
        cell: ({ row }) => (
          <div
            className="h-9 w-9 overflow-hidden rounded-full"
            style={{ background: 'var(--surface-1)' }}
          >
            {row.original.profileImageUrl ? (
              <img
                src={row.original.profileImageUrl}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <span
                className="flex h-full w-full items-center justify-center text-xs"
                style={{ color: 'var(--content-muted)' }}
                aria-hidden="true"
              >
                ?
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'stageName',
        accessorKey: 'stageName',
        header: () => t('artists.columns.stageName'),
        cell: ({ row }) => (
          <div className="flex max-w-[180px] flex-col">
            <span className="flex items-center gap-1">
              <span className="truncate text-sm font-medium text-[color:var(--content-strong)]" title={row.original.stageName}>
                {row.original.stageName}
              </span>
              {/* QA-562 쇼핑몰 아티스트 페이지(짧은 주소) 새 창. */}
              <ArtistStorefrontOpenLink artistId={row.original.artistId} stageName={row.original.stageName} />
            </span>
            {row.original.primaryAliasEn ? (
              <span className="truncate text-xs text-[color:var(--content-muted)]">
                {row.original.primaryAliasEn}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'type',
        header: () => t('artists.columns.type'),
        cell: ({ row }) => <ArtistTypeBadge type={row.original.type} />,
      },
      {
        id: 'status',
        header: () => t('artists.columns.status'),
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            <ArtistStatusPill status={row.original.status} artistId={row.original.artistId} />
            {/* QA-593 쇼핑몰 노출 중이면 [노출]. */}
            <ArtistDisplayedBadge displayed={artistDisplayedOf(row.original)} />
          </span>
        ),
      },
      {
        id: 'agency',
        accessorFn: (row) => row.fanClubName ?? '—',
        header: () => t('artists.columns.fanClub'),
      },
      {
        id: 'memberCount',
        accessorFn: (row) => (row.type === 'GROUP' ? row.memberCount : '—'),
        header: () => t('artists.columns.memberCount'),
      },
      {
        id: 'productCount',
        accessorKey: 'productCount',
        header: () => t('artists.columns.productCount'),
      },
      {
        id: 'createdAt',
        accessorFn: (row) => formatDate(row.createdAt),
        header: () => t('artists.columns.createdAt'),
      },
      // 246: 행 우측 비활성(소프트) + 영구 삭제(hard) 액션. 행 클릭(상세 진입)과 분리한다.
      ...(hasActions
        ? [
            {
              id: 'actions',
              header: () => t('artists.columns.actions'),
              cell: ({ row }: { row: { original: ArtistAdminListItem } }) => (
                <div
                  className="flex items-center justify-end gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* QA-593 노출 중 → [숨김], 숨김 → [노출]. BE 가 displayed 를 주기 전에는 숨긴다. */}
                  {displayToggleable && artistDisplayedOf(row.original) !== undefined ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={togglingDisplayedId === row.original.artistId}
                      data-testid="artist-displayed-toggle"
                      onClick={() => onToggleDisplayed?.(row.original.artistId, !artistDisplayedOf(row.original))}
                    >
                      {artistDisplayedOf(row.original) ? '숨김' : '노출'}
                    </Button>
                  ) : null}
                  {deletable && row.original.status === 'ACTIVE' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={deletingId === row.original.artistId}
                      data-testid="artist-delete-btn"
                      onClick={() => onDelete?.(row.original.artistId)}
                      aria-label={t('artists.actions.deleteRow', {
                        name: row.original.stageName,
                        defaultValue: t('artists.actions.archive'),
                      })}
                    >
                      {t('artists.actions.archive')}
                    </Button>
                  ) : null}
                  {purgeable ? (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={purgingId === row.original.artistId}
                      data-testid="artist-purge-btn"
                      onClick={() => onPurge?.(row.original.artistId)}
                      aria-label={t('artists.actions.purgeRow', {
                        name: row.original.stageName,
                        defaultValue: t('artists.actions.delete'),
                      })}
                    >
                      {t('artists.actions.delete')}
                    </Button>
                  ) : null}
                </div>
              ),
            } as ColumnDef<ArtistAdminListItem>,
          ]
        : []),
    ],
    [t, deletable, deletingId, onDelete, purgeable, purgingId, onPurge, displayToggleable, togglingDisplayedId, onToggleDisplayed],
  );

  const table = useReactTable({
    data: items as ArtistAdminListItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--surface-raised)',
          color: 'var(--content-muted)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <p>{t('artists.list.empty')}</p>
      </div>
    );
  }

  const goToDetail = (artistId: string) => navigate(`/artists/${artistId}`);

  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="artist-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const isSortable = ARTIST_SORTABLE_COLUMNS.has(header.column.id);
                const isActive = activeSortId === header.column.id;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSortable
                  ? isActive
                    ? activeSortDir === 'asc' ? 'ascending' : 'descending'
                    : 'none'
                  : 'none';
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    aria-sort={isSortable ? ariaSort : undefined}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                        onClick={() => onSortChange(toggleArtistSort(sort, header.column.id))}
                        aria-label={t('artists.list.sortBy', {
                          column: t(`artists.columns.${header.column.id}`),
                        })}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden="true">
                          {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              role="link"
              tabIndex={0}
              data-testid="artist-row"
              aria-label={t('artists.list.openRow', {
                name: row.original.stageName,
                defaultValue: row.original.stageName,
              })}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={() => goToDetail(row.original.artistId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToDetail(row.original.artistId);
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
